/**
 * #5250 PREVIEWSIGNIN: sign-in on a PR preview host.
 *
 * A preview is a version (or alias) of the staging worker on its own
 * workers.dev host. Google and Entra only return to the registered staging
 * host (the slot), so OAuth always finishes there; the slot then hands the
 * preview a single-use code bound to the user and to that preview host, and
 * the preview mints its own session, marked pv_ and refused on every other
 * host. The one place the client and the server agree on what a preview host
 * is, what the hand-off is called and how long each piece lives. Enforcement:
 * src/server/routes/auth-preview.ts and src/server/middleware/preview-session.ts.
 *
 * Round 2 (PR 5289 review): the hand-off is bound to the person and the
 * browser. The preview's start route sets a nonce cookie on the preview host
 * and sends only its SHA-256 to the slot; the slot's one confirm button asks
 * for a code carrying that hash; the claim spends it only in the browser that
 * holds the nonce.
 */

/** The staging slot: the one host the OAuth providers have registered. */
export const STAGING_SLOT_HOST = "crosbe-ai-staging.techsystem.workers.dev";
export const STAGING_SLOT_ORIGIN = `https://${STAGING_SLOT_HOST}`;

/**
 * A preview host: `<version or alias>-crosbe-ai-staging.techsystem.workers.dev`,
 * anchored at both ends and lower case only, so the slot itself,
 * `...workers.dev.evil.example` and any other Worker's host never match.
 */
export const PREVIEW_HOST_PATTERN = /^[a-z0-9][a-z0-9-]*-crosbe-ai-staging\.techsystem\.workers\.dev$/;

export function isPreviewHost(host: unknown): host is string {
  return typeof host === "string" && PREVIEW_HOST_PATTERN.test(host);
}

/** A hand-off code is spent within this long of the slot issuing it. */
export const PREVIEW_HANDOFF_TTL_MS = 60 * 1000;
/** A preview session ends this long after the claim, whatever its cookie or row say. */
export const PREVIEW_SESSION_MAX_MS = 12 * 60 * 60 * 1000;
/** Every preview session's token starts with this; better-auth's own tokens (32 alphanumerics) never do. */
export const PREVIEW_SESSION_TOKEN_PREFIX = "pv_";

export const PREVIEW_START_PATH = "/api/auth-preview/start";
export const PREVIEW_HANDOFF_PATH = "/api/auth-preview/handoff";
export const PREVIEW_CLAIM_PATH = "/api/auth-preview/claim";

/**
 * The preview browser's nonce, one cookie per sign-in attempt: set by the
 * start route on the preview host, read and cleared by that attempt's claim
 * only, so a second tab or a double click cannot overwrite the first
 * (round 3). `__Host-`, so no other Worker on techsystem.workers.dev can set
 * one for this host. The flow id in the name is the first 16 hex of the
 * nonce's hash, which the hand-off row already holds, so the claim finds its
 * own cookie without another parameter. Thirty minutes: the OAuth round trip
 * (better-auth's state lives ten) and the confirm, whose 60 s code starts
 * only when it is pressed.
 */
export const PREVIEW_NONCE_COOKIE_PREFIX = "__Host-crosbe-preview-handoff-";
export const PREVIEW_NONCE_MAX_AGE_S = 30 * 60;

/**
 * Round 4 (#5250): a start in a browser already carrying this many nonce
 * cookies expires them all before it sets its own, so abandoned attempts
 * cannot pile up for their 30 minutes (about 82 bytes each; the review's
 * probe reached 100 cookies and an 8 KB Cookie header). Five covers round 3's
 * two tabs with a double click in each, and a retry; an attempt older than
 * that was given up. A browser holds at most five, about 410 bytes.
 */
export const PREVIEW_NONCE_COOKIE_CAP = 5;

/** The nonce cookie of the attempt whose nonce hashes to `nonceHash`. */
export function previewNonceCookieName(nonceHash: string): string {
  return `${PREVIEW_NONCE_COOKIE_PREFIX}${nonceHash.slice(0, 16)}`;
}
/** What crosses to the slot instead of the nonce: its SHA-256, lower-case hex. */
export const NONCE_HASH_PATTERN = /^[0-9a-f]{64}$/;

export function isNonceHash(value: unknown): value is string {
  return typeof value === "string" && NONCE_HASH_PATTERN.test(value);
}

/** The refusals a person can land on; /auth-error explains each. */
export const PREVIEW_SIGNIN_ERRORS = ["code_used", "host_not_preview", "handoff_expired", "handoff_other_browser"] as const;
export type PreviewSigninError = (typeof PREVIEW_SIGNIN_ERRORS)[number];

/** The query parameter that carries the preview host through /login and the OAuth callback on the slot. */
export const HANDOFF_PARAM = "handoff";
/** The one beside it: the preview browser's nonce hash. */
export const NONCE_HASH_PARAM = "nonce_hash";

export type PreviewProvider = "google" | "microsoft";

/** On a preview host: the start route, which sets the nonce and sends the browser to the slot. */
export function previewStartPath(returnTo: string): string {
  return `${PREVIEW_START_PATH}?${new URLSearchParams({ returnTo }).toString()}`;
}

/** The slot's /login, asked to hand the session back to `previewHost`, in the browser whose nonce hashes to `nonceHash`, once sign-in finishes. */
export function slotLoginUrl(previewHost: string, returnTo: string, nonceHash: string): string {
  const params = new URLSearchParams({ [HANDOFF_PARAM]: previewHost, [NONCE_HASH_PARAM]: nonceHash, returnTo });
  return `${STAGING_SLOT_ORIGIN}/login?${params.toString()}`;
}

/** Where the slot sends the browser with a fresh code: the preview's own claim route. */
export function previewClaimUrl(previewHost: string, code: string, returnTo: string): string {
  const params = new URLSearchParams({ code, returnTo });
  return `https://${previewHost}${PREVIEW_CLAIM_PATH}?${params.toString()}`;
}
